import { useMemo, useState } from 'react'
import { messageOf } from '../../lib/errors'
import { useReportDirty } from './TraySection'
import {
  demandCsv,
  loadItemDemand,
  setItemPledges,
  type DemandRow,
  type ManagedItem,
} from '../../lib/shop'

/**
 * 129 — the two admin pieces a pre-order was missing besides its window:
 * what the buyer agrees to, and the count you order from the supplier.
 */

const MAX_PLEDGES = 5

/**
 * Statements a buyer must tick to order the item ("I will take part in the
 * 2027 Celebrate Your Wins run").
 *
 * A REPLACE (049): the whole list is sent, and an empty list removes them all.
 * An unchanged statement keeps its id on the server, so re-saving the same
 * words does not refuse every buyer whose page is already open; REWORDING one
 * does refuse them — they agreed to words that no longer exist — and the
 * orders already placed keep the words they agreed to.
 */
export function ItemPledges({
  item,
  onError,
  onChanged,
  onDirty,
}: {
  item: ManagedItem
  onError: (m: string | null) => void
  onChanged: () => void
  onDirty?: (d: boolean) => void
}) {
  const saved = item.pledges.map((p) => p.statement)
  const [lines, setLines] = useState<string[]>(saved)
  const [busy, setBusy] = useState(false)

  const cleaned = lines.map((l) => l.trim())
  const tooShort = cleaned.some((l) => l.length > 0 && l.length < 3)
  const blank = cleaned.some((l) => l.length === 0)
  const dupes = new Set(cleaned.map((l) => l.toLowerCase())).size !== cleaned.length
  const changed = cleaned.length !== saved.length || cleaned.some((l, i) => l !== saved[i])
  const bad = tooShort || blank || dupes
  useReportDirty(changed, onDirty)

  async function save() {
    setBusy(true)
    onError(null)
    try {
      await setItemPledges(item.itemId, cleaned)
      onChanged()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shopitems__options">
      <h4 className="shopitems__subhead">What the buyer agrees to</h4>
      {lines.length === 0 ? (
        <p className="admin__hint">
          Nothing. Add a statement and every order button on this item waits
          until the buyer agrees to it — "I will take part in the Celebrate Your
          Wins run", say. What they agreed to is kept on their order.
        </p>
      ) : (
        <ol className="shopitems__pledges">
          {lines.map((l, i) => (
            <li key={i} className="shopitems__pledge">
              <input
                value={l}
                maxLength={280}
                disabled={busy}
                aria-label={`Statement ${i + 1}`}
                placeholder="I will take part in the Celebrate Your Wins run"
                onChange={(e) =>
                  setLines((ls) => ls.map((x, j) => (j === i ? e.target.value : x)))
                }
              />
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                aria-label={`Remove statement ${i + 1}`}
                onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </li>
          ))}
        </ol>
      )}

      {(tooShort || dupes) && (
        <p className="shopitems__refusal" role="alert">
          {dupes ? 'The same statement appears twice.' : 'A statement needs at least three characters.'}
        </p>
      )}

      <div className="shopitems__actions">
        <button
          type="button"
          className="btn btn--sm"
          disabled={busy || lines.length >= MAX_PLEDGES}
          onClick={() => setLines((ls) => [...ls, ''])}
        >
          Add a statement
        </button>
        <button
          type="button"
          className="btn btn--sm btn--primary"
          disabled={busy || !changed || bad}
          onClick={save}
        >
          {busy ? 'Saving…' : changed ? 'Save statements' : 'Saved'}
        </button>
      </div>
      {saved.length > 0 && changed && (
        <p className="admin__hint">
          Rewording a statement asks anyone with this item open to agree again.
          Orders already placed keep the words they agreed to.
        </p>
      )}
    </div>
  )
}

/**
 * The tally the supplier order is sized from: combinations down, stores
 * across, WAITING orders in the cells (the ones with no box yet).
 *
 * Loaded on demand rather than with the catalogue — it is a question asked a
 * few times a year, and `shop_item_demand` refuses anybody who may not edit
 * the item rather than quietly narrowing to their own stores.
 *
 * The CSV is the LONG shape (one row per store per combination): it is the
 * one a supplier's or a coordinator's spreadsheet pivots, and a grid has to be
 * unpicked before anything can be done with it.
 */
export function ItemDemand({
  item,
  onError,
}: {
  item: ManagedItem
  onError: (m: string | null) => void
}) {
  const [rows, setRows] = useState<DemandRow[] | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    setBusy(true)
    onError(null)
    try {
      setRows(await loadItemDemand(item.itemId))
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const grid = useMemo(() => {
    if (!rows) return null
    const stores = [...new Map(rows.map((r) => [r.stockRoomId, r.roomName])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
    const combos = [...new Map(rows.map((r) => [r.variantId, r])).values()].sort(
      (a, b) =>
        (a.sizeRank ?? 999) - (b.sizeRank ?? 999) ||
        a.sortOrder - b.sortOrder ||
        a.variantLabel.localeCompare(b.variantLabel),
    )
    const cell = new Map(rows.map((r) => [`${r.variantId}|${r.stockRoomId}`, r.awaiting]))
    const byStore = new Map<string, number>()
    const byCombo = new Map<string, number>()
    for (const r of rows) {
      byStore.set(r.stockRoomId, (byStore.get(r.stockRoomId) ?? 0) + r.awaiting)
      byCombo.set(r.variantId, (byCombo.get(r.variantId) ?? 0) + r.awaiting)
    }
    const total = rows.reduce((n, r) => n + r.awaiting, 0)
    const held = rows.reduce((n, r) => n + r.held, 0)
    const collected = rows.reduce((n, r) => n + r.collected, 0)
    return { stores, combos, cell, byStore, byCombo, total, held, collected }
  }, [rows])

  function download() {
    if (!rows) return
    const blob = new Blob([demandCsv(item.name, rows)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${item.code}-orders-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="shopitems__options">
      <h4 className="shopitems__subhead">Orders by store</h4>
      {!grid ? (
        <>
          <p className="admin__hint">
            Every live order for this item, by combination and store — what to
            order from the supplier.
          </p>
          <button type="button" className="btn btn--sm" disabled={busy} onClick={load}>
            {busy ? 'Counting…' : 'Count the orders'}
          </button>
        </>
      ) : grid.combos.length === 0 ? (
        <p className="admin__hint">No live orders for this item yet.</p>
      ) : (
        <>
          <p className="admin__hint">
            <strong>{grid.total}</strong> waiting for stock
            {grid.held > 0 && <> · {grid.held} reserved from the shelf</>}
            {grid.collected > 0 && <> · {grid.collected} collected</>}. The cells
            count the waiting ones: that is what to order. Cancelled orders are
            not counted, and a buyer can still cancel after the cutoff.
          </p>
          <div className="shopitems__demandwrap">
            <table className="admin__table shopitems__demand">
              <thead>
                <tr>
                  <th scope="col">Combination</th>
                  {grid.stores.map(([id, name]) => (
                    <th scope="col" key={id}>{name}</th>
                  ))}
                  <th scope="col">Total</th>
                </tr>
              </thead>
              <tbody>
                {grid.combos.map((c) => (
                  <tr key={c.variantId}>
                    <th scope="row">{c.variantLabel}</th>
                    {grid.stores.map(([id]) => {
                      const n = grid.cell.get(`${c.variantId}|${id}`) ?? 0
                      return <td key={id}>{n > 0 ? n : '—'}</td>
                    })}
                    <td><strong>{grid.byCombo.get(c.variantId) ?? 0}</strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  {grid.stores.map(([id]) => (
                    <td key={id}><strong>{grid.byStore.get(id) ?? 0}</strong></td>
                  ))}
                  <td><strong>{grid.total}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="shopitems__actions">
            <button type="button" className="btn btn--sm" onClick={download}>Download CSV</button>
            <button type="button" className="btn btn--sm" disabled={busy} onClick={load}>
              {busy ? 'Counting…' : 'Count again'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
